import EngagementIcon from '@assets/icons/EngagementIcon'
import UnlockIcon from '@assets/icons/UnlockIcon'
import { useApps } from '@auth/useApps'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ErrorText from '@shared/forms/ErrorText'
import { AppPermission } from '@shared/permissions'
import MessageAndAttachmentsSection from '@tenant/recognition/RecognitionModal/sendRecognitionView/MessageAndAttachmentsSection'
import { RecognitionFormData } from '@tenant/recognition/RecognitionModal/sendRecognitionView/useSendRecognitionForm'
import { T } from '@transifex/react'
import { format, parseISO } from 'date-fns'
import { useFormContext, useWatch } from 'react-hook-form'
import { MobileRecognitionDrawerView } from '../useMobileRecognitionDrawer'
import MobileRecognitionRecipients from './MobileRecognitionRecipients'
import SendRecognitionNavMenuItem from './SendRecognitionNavMenuItem'

type MobileRecognitionSendViewProps = {
  hasQualities: boolean
}

const MobileRecognitionSendView = ({ hasQualities }: MobileRecognitionSendViewProps) => {
  const { control, formState } = useFormContext<RecognitionFormData>()
  const [hasAppPermission] = useApps()
  const recipients = useWatch({ control: control, name: 'sendTo' })
  const qualities = useWatch({ control, name: 'qualities' }) ?? []
  const points = useWatch({ control, name: 'amount' }) ?? 0
  const tenantWide = useWatch({ control, name: 'tenantWide' }) ?? true
  const scheduledAt = useWatch({ control, name: 'scheduledAt' })
  const scheduledDate = scheduledAt && parseISO(scheduledAt)

  const recipientsErrorMessage = formState.errors.sendTo?.message
    ? String(formState.errors.sendTo.message)
    : undefined
  const qualitiesErrorMessage = formState.errors.qualities?.message
    ? String(formState.errors.qualities.message)
    : undefined
  const pointsErrorMessage = formState.errors.amount?.message
    ? String(formState.errors.amount.message)
    : undefined
  const scheduledAtErrorMessage = formState.errors.scheduledAt?.message
    ? String(formState.errors.scheduledAt.message)
    : undefined

  const qualitiesSummary = (() => {
    if (qualities.length === 0) return null
    if (qualities.length === 1) return qualities[0].name
    return `${qualities[0].name} +${qualities.length - 1}`
  })()
  const canSendPoints = hasAppPermission('pointsApp', AppPermission.PointsAppSendPoints)
  const canScheduleRecognition = hasAppPermission(
    'recognitionApp',
    AppPermission.RecognitionAppScheduleRecognition,
  )
  const canRefineMessage = hasAppPermission(
    'refinementApp',
    AppPermission.RefinementAppRefineRecognition,
  )
  const canUpgradeLicense = hasAppPermission(
    'refinementApp',
    AppPermission.RefinementAppUpgradeLicense,
  )

  return (
    <Stack gap={2}>
      <MobileRecognitionRecipients recipients={recipients} />
      {!!recipientsErrorMessage && <ErrorText>{recipientsErrorMessage}</ErrorText>}
      <MessageAndAttachmentsSection
        control={control}
        canRefineMessage={canRefineMessage}
        canUpgradeLicense={canUpgradeLicense}
      />
      <Stack gap={1}>
        {hasQualities && (
          <SendRecognitionNavMenuItem
            view={MobileRecognitionDrawerView.QUALITIES}
            icon={<AddCircleOutlineOutlinedIcon />}
            label={<T _str="Add qualities" />}
            errorMessage={qualitiesErrorMessage}>
            {qualitiesSummary !== null && (
              <Typography variant="caption">{qualitiesSummary}</Typography>
            )}
          </SendRecognitionNavMenuItem>
        )}
        {canSendPoints && (
          <SendRecognitionNavMenuItem
            view={MobileRecognitionDrawerView.POINTS}
            icon={<EngagementIcon />}
            label={<T _str="Add points" />}
            errorMessage={pointsErrorMessage}>
            <Typography variant="caption">{points.toLocaleString()}</Typography>
          </SendRecognitionNavMenuItem>
        )}
        <SendRecognitionNavMenuItem
          view={MobileRecognitionDrawerView.VISIBILITY}
          icon={<UnlockIcon />}
          label={<T _str="Set post visibility" />}>
          <Typography variant="caption">
            {tenantWide ? <T _str="Public" /> : <T _str="Private" />}
          </Typography>
        </SendRecognitionNavMenuItem>
        {canScheduleRecognition && (
          <SendRecognitionNavMenuItem
            view={MobileRecognitionDrawerView.SCHEDULE}
            icon={<AccessTimeOutlinedIcon />}
            label={<T _str="Schedule post" />}
            errorMessage={scheduledAtErrorMessage}>
            <Typography variant="caption">
              {!!scheduledDate ? (
                format(scheduledDate, 'dd/LL/yyyy h:mm a')
              ) : (
                <T _str="Immediately" />
              )}
            </Typography>
          </SendRecognitionNavMenuItem>
        )}
      </Stack>
    </Stack>
  )
}

export default MobileRecognitionSendView
